import { queryClient } from "@/src/config/react-query";
import {
  GroupMemberRole,
  JoinType,
  MembershipStatus,
} from "@/src/constants/groups/group-membership";
import { GroupEditProps } from "@/src/models/group.model";
import { InvitableMember, UserProfile } from "@/src/models/user.model";
import {
  useCreateGroup,
  useCreateGroupMembership,
  useUpdateGroup,
  useUpdateGroupMemberCount,
} from "@/src/services/api/groups/groups.hooks";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import * as ImagePicker from "expo-image-picker";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUploadFile } from "../use-upload-file";

export const useGroupForm = (
  mode: "create" | "edit",
  initialValues: GroupEditProps,
  currentUser: UserProfile,
  callbacks?: {
    onSuccess?: () => void;
    onError?: (message?: string) => void;
  },
) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, onChangeName] = useState(initialValues.name);
  const [desc, onChangeDesc] = useState(initialValues.description);
  const [isPrivate, setIsPrivate] = useState(initialValues.isPrivate ?? false);
  const [selectedImage, setSelectedImage] = useState<
    | { uri: string; fileName: string; mimeType: string; isLocal: boolean }
    | undefined
  >(
    initialValues.coverImageUrl
      ? {
          uri: initialValues.coverImageUrl,
          fileName: "",
          mimeType: "",
          isLocal: false,
        }
      : undefined,
  );

  const [membersModalVisible, setMembersModalVisible] = useState(false);
  const [invitedMembers, setInvitedMembers] = useState<InvitableMember[]>([]);

  const { mutateAsync: createGroup } = useCreateGroup();
  const { mutateAsync: updateGroup } = useUpdateGroup();
  const { mutateAsync: createGroupMembership } = useCreateGroupMembership();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: updateMemberCount } = useUpdateGroupMemberCount();
  const { mutate: sendNotification } = useSendNotification();

  const handleInvite = (members: InvitableMember[]) =>
    setInvitedMembers(members);
  const removeInvited = (id: string) =>
    setInvitedMembers((prev) => prev.filter((m) => m.itemId !== id));

  const handleSubmit = async () => {
    if (!name.trim()) {
      callbacks?.onError?.("ERROR_NAME_REQUIRED");
      return;
    }
    setIsSubmitting(true);
    try {
      let coverImageUrl = selectedImage?.uri;
      if (selectedImage?.isLocal) {
        const uploadedUrl = await uploadFile({
          uri: selectedImage.uri,
          fileName: selectedImage.fileName,
          mimeType: selectedImage.mimeType,
        });
        coverImageUrl = uploadedUrl?.split("?")[0];
      }
      if (mode === "create") {
        try {
          const data = await createGroup({
            name,
            description: desc,
            coverImageUrl,
            isPrivate,
          });

          const groupId = data.insertgroup.itemId;

          await createGroupMembership({
            groupId,
            userId: currentUser.itemId,
            role: GroupMemberRole.ADMIN,
            status: MembershipStatus.ACTIVE,
            joinType: JoinType.DIRECT_JOIN,
            groupUserSnapshot: {
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              email: currentUser.email,
              profileImageUrl: currentUser.profileImageUrl,
            },
            joinedAt: new Date().toLocaleDateString("en-GB"),
          });

          await Promise.all(
            invitedMembers.map((member) =>
              createGroupMembership({
                groupId,
                userId: member.itemId,
                role: GroupMemberRole.MEMBER,
                status: MembershipStatus.ACTIVE,
                joinType: JoinType.ADDED_BY_ADMIN,
                groupUserSnapshot: {
                  firstName: member.firstName,
                  lastName: member.lastName,
                  email: member.email,
                  profileImageUrl: member.profileImageUrl,
                },
                joinedAt: new Date().toLocaleDateString("en-GB"),
              }),
            ),
          );

          // Notify each invited member they've been added to the group
          if (invitedMembers.length > 0) {
            sendNotification({
              userIds: invitedMembers.map((m) => m.itemId),
              denormalizedPayload: JSON.stringify({
                type: "added_to_group",
                message: t("NOTIFY_ADDED_TO_GROUP", {
                  firstName: currentUser.firstName,
                  lastName: currentUser.lastName,
                  groupName: name,
                }),
                actorAvatar: currentUser.profileImageUrl,
                actorName: `${currentUser.firstName} ${currentUser.lastName}`,
                actorId: currentUser.itemId,
                groupId,
                targetEntityId: groupId,
              }),
              configuratoinName: "add-to-group",
              responseKey: "group-action",
              responseValue: "added_to_group",
            });
          }

          await updateMemberCount(groupId);
          queryClient.invalidateQueries({ queryKey: ["getGroups"] });
          queryClient.invalidateQueries({ queryKey: ["getGroupById"] });
          callbacks?.onSuccess?.();
        } catch {
          callbacks?.onError?.();
        }
      } else {
        try {
          await updateGroup({
            name,
            description: desc,
            coverImageUrl,
            filter: JSON.stringify({ ItemId: initialValues.ItemId }),
            isPrivate: isPrivate
          });
          queryClient.invalidateQueries({ queryKey: ["getGroups"] });
          queryClient.invalidateQueries({ queryKey: ["getGroupById"] });
          callbacks?.onSuccess?.();
        } catch {
          callbacks?.onError?.();
        } finally {
          setIsSubmitting(false);
        }
      }
      callbacks?.onSuccess?.();
    } catch {
      callbacks?.onError?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const pickImageAsync = async () => {
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      allowsEditing: true,
      quality: 1,
    });

    if (!result.canceled) {
      setSelectedImage({
        uri: result.assets[0].uri,
        fileName: result.assets[0].file?.name ?? `upload_${Date.now()}`,
        mimeType: result.assets[0].file?.type ?? "",
        isLocal: true,
      });
    }
  };

  return {
    name,
    onChangeName,
    desc,
    onChangeDesc,
    selectedImage,
    setSelectedImage,
    membersModalVisible,
    setMembersModalVisible,
    invitedMembers,
    isPending: isSubmitting,
    handleInvite,
    removeInvited,
    pickImageAsync,
    isPrivate,
    setIsPrivate,
    handleSubmit,
  };
};
